import { useState } from 'react';

export default function LevelTestRegistration() {
  const [formData, setFormData] = useState({
    studentName: '',
    parentName: '',
    address: '',
    dateOfBirth: '',
    age: '',
    schoolYear: '',
    schoolAttending: '',
    countryOfOrigin: '',
    languageProficiency: '',
    learningBackground: '',
    enrollmentAim: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4gk4lltnoqs9bvm9ks0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          studentName: '',
          parentName: '',
          address: '',
          dateOfBirth: '',
          age: '',
          schoolYear: '',
          schoolAttending: '',
          countryOfOrigin: '',
          languageProficiency: '',
          learningBackground: '',
          enrollmentAim: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#F5F1E8]">
      {/* Header */}
      <section className="bg-[#153223] text-white py-16 relative">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="font-['Yeseva_One',serif] text-4xl md:text-5xl text-[#FFE66D] mb-4">
            Free Online Level Test Registration
          </h1>
          <p className="text-lg md:text-xl text-[#F5F1E8] leading-relaxed">
            免费在线水平测试注册 • 무료 온라인 레벨 테스트 등록 • 無料オンラインレベルテスト登録
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <form id="level-test-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
              
              {/* Student's Name */}
              <div>
                <label htmlFor="studentName" className="block text-lg font-semibold text-[#153223] mb-2">
                  1. Student's Name
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    学生姓名 • 학생 이름 • 生徒の名前
                  </span>
                </label>
                <input
                  type="text"
                  id="studentName"
                  name="studentName"
                  value={formData.studentName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base"
                  placeholder="Enter student's full name"
                />
              </div>

              {/* Parent's Name */}
              <div>
                <label htmlFor="parentName" className="block text-lg font-semibold text-[#153223] mb-2">
                  2. Parent's Name (for us to call you by)
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    家长姓名 • 학부모 이름 • 保護者の名前
                  </span>
                </label>
                <input
                  type="text"
                  id="parentName"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base"
                  placeholder="Enter parent's name"
                />
              </div>

              {/* Current Residence Address */}
              <div>
                <label htmlFor="address" className="block text-lg font-semibold text-[#153223] mb-2">
                  3. Current Residence Address
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    当前居住地址 • 현재 거주 주소 • 現在の住所
                  </span>
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base"
                  placeholder="Enter full address"
                />
              </div>

              {/* Student Date of Birth */}
              <div>
                <label htmlFor="dateOfBirth" className="block text-lg font-semibold text-[#153223] mb-2">
                  4. Student Date of Birth
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    学生出生日期 • 학생 생년월일 • 生徒の生年月日
                  </span>
                </label>
                <input
                  type="date"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base"
                />
              </div>

              {/* Age */}
              <div>
                <label htmlFor="age" className="block text-lg font-semibold text-[#153223] mb-2">
                  5. Age
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    年龄 • 나이 • 年齢
                  </span>
                </label>
                <input
                  type="number"
                  id="age"
                  name="age"
                  value={formData.age}
                  onChange={handleChange}
                  required
                  min="6"
                  max="14"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base"
                  placeholder="Enter age (6-14)"
                />
              </div>

              {/* School Year */}
              <div>
                <label htmlFor="schoolYear" className="block text-lg font-semibold text-[#153223] mb-2">
                  6. School Year
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    年级 • 학년 • 学年
                  </span>
                </label>
                <input
                  type="text"
                  id="schoolYear"
                  name="schoolYear"
                  value={formData.schoolYear}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base"
                  placeholder="e.g., Year 1, Grade 3, Standard 5"
                />
              </div>

              {/* School Attending */}
              <div>
                <label htmlFor="schoolAttending" className="block text-lg font-semibold text-[#153223] mb-2">
                  7. School Attending
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    就读学校 • 재학 중인 학교 • 在籍校
                  </span>
                </label>
                <input
                  type="text"
                  id="schoolAttending"
                  name="schoolAttending"
                  value={formData.schoolAttending}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base"
                  placeholder="Enter school name"
                />
              </div>

              {/* Country of Origin */}
              <div>
                <label htmlFor="countryOfOrigin" className="block text-lg font-semibold text-[#153223] mb-2">
                  8. Country of Origin
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    原籍国 • 출신 국가 • 出身国
                  </span>
                </label>
                <input
                  type="text"
                  id="countryOfOrigin"
                  name="countryOfOrigin"
                  value={formData.countryOfOrigin}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base"
                  placeholder="e.g., Malaysia, Korea, Japan, China"
                />
              </div>

              {/* Language Proficiency */}
              <div>
                <label htmlFor="languageProficiency" className="block text-lg font-semibold text-[#153223] mb-2">
                  9. Language Proficiency (just a rough idea)
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    语言水平 • 언어 능력 • 言語能力
                  </span>
                </label>
                <select
                  id="languageProficiency"
                  name="languageProficiency"
                  value={formData.languageProficiency}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 pr-8 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base cursor-pointer"
                >
                  <option value="">Select proficiency level</option>
                  <option value="Beginner">Beginner (初级 • 초급 • 初級)</option>
                  <option value="Intermediate">Intermediate (中级 • 중급 • 中級)</option>
                  <option value="Advanced">Advanced (高级 • 고급 • 上級)</option>
                </select>
              </div>

              {/* Student's English Language Learning Background */}
              <div>
                <label htmlFor="learningBackground" className="block text-lg font-semibold text-[#153223] mb-2">
                  10. Student's English language learning background
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    英语学习背景 • 영어 학습 배경 • 英語学習歴
                  </span>
                </label>
                <textarea
                  id="learningBackground"
                  name="learningBackground"
                  value={formData.learningBackground}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base resize-none"
                  placeholder="Please share your child's English learning experience (max 500 characters)"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.learningBackground.length}/500 characters</p>
              </div>

              {/* Aim for Enrolling */}
              <div>
                <label htmlFor="enrollmentAim" className="block text-lg font-semibold text-[#153223] mb-2">
                  11. What is the aim for enrolling your child?
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    报名目的 • 등록 목적 • 入学目的
                  </span>
                </label>
                <textarea
                  id="enrollmentAim"
                  name="enrollmentAim"
                  value={formData.enrollmentAim}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base resize-none"
                  placeholder="Please share your goals and expectations (max 500 characters)"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.enrollmentAim.length}/500 characters</p>
              </div>

              {/* Additional Information */}
              <div>
                <label htmlFor="additionalInfo" className="block text-lg font-semibold text-[#153223] mb-2">
                  12. Any additional information to let us know?
                  <span className="text-sm font-normal text-gray-600 ml-3">
                    其他信息 • 추가 정보 • その他の情報
                  </span>
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#153223] focus:outline-none text-base resize-none"
                  placeholder="Any special needs, preferences, or questions? (max 500 characters)"
                />
                <p className="text-sm text-gray-500 mt-1">{formData.additionalInfo.length}/500 characters</p>
              </div>

              {/* Submit Button */}
              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="whitespace-nowrap w-full bg-[#153223] hover:bg-[#1f4430] text-white font-bold px-8 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                </button>
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-500 text-green-800 px-6 py-4 rounded-lg">
                  <p className="font-semibold">✓ Registration submitted successfully!</p>
                  <p className="text-sm mt-1">We will contact you soon to schedule the level test.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-500 text-red-800 px-6 py-4 rounded-lg">
                  <p className="font-semibold">✗ Submission failed</p>
                  <p className="text-sm mt-1">Please try again or contact us directly via WhatsApp.</p>
                </div>
              )}
            </form>
          </div>

          {/* Contact Information */}
          <div className="mt-12 bg-white rounded-2xl shadow-xl p-8">
            <h3 className="text-2xl font-bold text-[#153223] mb-6 text-center">
              Contact Us Directly
              <span className="block text-base font-normal text-gray-600 mt-2">
                直接联系我们 • 직접 문의하기 • 直接お問い合わせ
              </span>
            </h3>
            
            <div className="flex flex-col md:flex-row gap-6 justify-center items-center">
              {/* WhatsApp */}
              <a
                href="https://wa.me/60198950116"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-[#25D366] hover:bg-[#20BA5A] text-white px-6 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-fill text-2xl"></i>
                <span className="font-semibold">+6019-895-0116</span>
              </a>

              {/* Instagram */}
              <a
                href="https://instagram.com/oht.tuition"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-6 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
              >
                <i className="ri-instagram-fill text-2xl"></i>
                <span className="font-semibold">@oht.tuition</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
